/**
 * Conditional Logic Utilities
 * Handles showing/hiding questions based on previous answers
 */

import { Question, FormAnswers } from '../types/formConfig'

/**
 * Evaluates whether a question should be displayed based on conditional logic
 */
export const shouldDisplayQuestion = (
  question: Question,
  answers: FormAnswers
): boolean => {
  // If no conditional display rules, always show the question
  if (!question.conditionalDisplay) {
    return true
  }

  const conditions = question.conditionalDisplay

  // Check all conditions - ALL must be satisfied (AND logic)
  for (const [questionId, acceptableValues] of Object.entries(conditions)) {
    const userAnswer = answers[questionId]

    // If the dependency question hasn't been answered yet, don't show this question
    if (userAnswer === undefined || userAnswer === null || userAnswer === '') {
      return false
    }

    // Handle array answers (checkbox questions)
    if (Array.isArray(userAnswer)) {
      // Check if any of the user's selected values are in the acceptable values
      const hasMatch = userAnswer.some(value => acceptableValues.includes(String(value)))
      if (!hasMatch) {
        return false
      }
    } else {
      // For single value answers, check if it's in the acceptable values
      if (!acceptableValues.includes(String(userAnswer))) {
        return false
      }
    }
  }

  // All conditions satisfied
  return true
}

/**
 * Gets only the questions that should be visible based on current answers
 */
export const getVisibleQuestions = (
  allQuestions: Question[],
  answers: FormAnswers
): Question[] => {
  return allQuestions.filter(question => shouldDisplayQuestion(question, answers))
}

/**
 * Gets the index of a question in the visible questions list
 */
export const getVisibleQuestionIndex = (
  allQuestions: Question[],
  answers: FormAnswers,
  questionId: string
): number => {
  const visibleQuestions = getVisibleQuestions(allQuestions, answers)
  return visibleQuestions.findIndex(q => q.id === questionId)
}

/**
 * Gets the total number of visible questions
 */
export const getTotalVisibleQuestions = (
  allQuestions: Question[],
  answers: FormAnswers
): number => {
  return getVisibleQuestions(allQuestions, answers).length
}

/**
 * Checks if all required visible questions have been answered
 */
export const areAllRequiredQuestionsAnswered = (
  allQuestions: Question[],
  answers: FormAnswers
): boolean => {
  const visibleQuestions = getVisibleQuestions(allQuestions, answers)

  return visibleQuestions.every(question => {
    if (!question.required) {
      return true
    }

    const answer = answers[question.id]

    // Check if answer exists and is not empty
    if (answer === undefined || answer === null || answer === '') {
      return false
    }

    // For array answers (checkbox), ensure at least one option is selected
    if (Array.isArray(answer)) {
      return answer.length > 0
    }

    return true
  })
}

/**
 * Gets the next unanswered question index
 */
export const getNextUnansweredQuestionIndex = (
  allQuestions: Question[],
  answers: FormAnswers
): number => {
  const visibleQuestions = getVisibleQuestions(allQuestions, answers)

  const index = visibleQuestions.findIndex(question => {
    const answer = answers[question.id]

    if (answer === undefined || answer === null || answer === '') {
      return true
    }

    if (Array.isArray(answer) && answer.length === 0) {
      return true
    }

    return false
  })

  // If all questions are answered, return the last question index
  return index === -1 ? visibleQuestions.length - 1 : index
}
